import React from 'react';
import { getAvatarById } from '../utils/avatarUtils';

interface AvatarDisplayProps {
  user: {
    avatarId?: string;
    avatarUrl?: string;
    name?: string;
    nickname?: string;
  };
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  alt?: string;
}

const AvatarDisplay: React.FC<AvatarDisplayProps> = ({
  user,
  size = 'md',
  className = '',
  alt
}) => {
  const sizeClasses = {
    sm: 'h-8 w-8 text-sm',
    md: 'h-12 w-12 text-lg',
    lg: 'h-16 w-16 text-2xl',
    xl: 'h-20 w-20 text-3xl'
  };

  const baseClasses = `rounded-full flex items-center justify-center ${sizeClasses[size]} ${className}`;

  // If user has a selected avatar from the new system
  if (user.avatarId) {
    const avatarData = getAvatarById(user.avatarId);
    if (avatarData) {
      return (
        <div 
          className={`${baseClasses} bg-gradient-to-br from-indigo-400 to-purple-500`}
          style={{ 
            backgroundColor: avatarData.color,
            background: `linear-gradient(135deg, ${avatarData.color} 0%, ${avatarData.color}CC 100%)`
          }}
          title={avatarData.name}
        >
          {avatarData.emoji}
        </div>
      );
    }
  }

  // Fallback to old avatarUrl system for backward compatibility
  if (user.avatarUrl) {
    return (
      <img
        className={`${baseClasses} object-cover`}
        src={user.avatarUrl}
        alt={alt || `${user.name || user.nickname || 'User'}'s avatar`}
      />
    );
  }

  // Default fallback - show first letter of name
  const displayName = user.name || user.nickname || 'User';
  const firstLetter = displayName.charAt(0).toUpperCase();
  
  return (
    <div className={`${baseClasses} bg-indigo-100 text-indigo-600 font-medium`}>
      {firstLetter}
    </div>
  );
};

export default AvatarDisplay;
